import { useState } from "react"
import Layout from "@/components/Layout"
import DepreciationScheduleTable, { ScheduleRow } from "@/components/DepreciationScheduleTable"
import { exportToCSV } from "@/lib/exportCsv"
import {Armchair} from 'lucide-react'

function fmt(n: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n)
}

const FURNITURE_TYPES = [
  { label: "Office Furniture (7-yr)", life: 7 },
  { label: "Office Fixtures (7-yr)", life: 7 },
  { label: "Commercial Appliances (5-yr)", life: 5 },
  { label: "Carpeting / Flooring (7-yr)", life: 7 },
  { label: "Residential Appliances (5-yr)", life: 5 },
]

export default function FurnitureAppliancesCalculator() {
  const [cost, setCost] = useState("")
  const [salvage, setSalvage] = useState("")
  const [method, setMethod] = useState("sl")
  const [fType, setFType] = useState(0)
  const [schedule, setSchedule] = useState<ScheduleRow[]>([])
  const [results, setResults] = useState<{ yr1: number; totalDepr: number } | null>(null)
  const [error, setError] = useState("")

  function calculate() {
    setError("")
    const c = parseFloat(cost)
    const s = parseFloat(salvage) || 0
    const { life } = FURNITURE_TYPES[fType]
    if (isNaN(c) || c <= 0) { setError("Please enter a valid asset cost."); return }
    if (s >= c) { setError("Salvage value must be less than cost."); return }

    const rows: ScheduleRow[] = []
    let accumulated = 0

    if (method === "sl") {
      const annual = (c - s) / life
      for (let i = 1; i <= life; i++) {
        const begin = i === 1 ? c : c - accumulated
        accumulated += annual
        rows.push({ year: i, beginningValue: begin, depreciation: annual, accumulatedDepreciation: accumulated, endingBookValue: Math.max(s, c - accumulated) })
      }
    } else {
      const ddbRate = 2 / life
      let bookValue = c
      for (let i = 1; i <= life; i++) {
        const begin = bookValue
        let depr = bookValue * ddbRate
        const slDepr = (bookValue - s) / (life - i + 1)
        if (slDepr > depr) depr = slDepr
        if (bookValue - depr < s) depr = bookValue - s
        if (depr < 0) depr = 0
        accumulated += depr
        bookValue -= depr
        rows.push({ year: i, beginningValue: begin, depreciation: depr, accumulatedDepreciation: accumulated, endingBookValue: bookValue })
        if (bookValue <= s) break
      }
    }

    setSchedule(rows)
    setResults({ yr1: rows[0]?.depreciation ?? 0, totalDepr: c - s })
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-3">
            <div className="bg-orange-600 rounded-lg p-2">
              <Armchair className="w-6 h-6 text-white" />
            </div>
            <span className="text-sm font-medium text-orange-600 uppercase tracking-wide">Asset Specific</span>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            Furniture & Appliance Depreciation Calculator
          </h1>
          <p className="text-lg text-gray-600">
            Calculate depreciation for office furniture, fixtures, and appliances. Most office
            furniture is classified as 7-year MACRS property.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Enter Asset Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Asset Type</label>
              <select value={fType} onChange={(e) => setFType(parseInt(e.target.value))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-900">
                {FURNITURE_TYPES.map((f, i) => <option key={f.label} value={i}>{f.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Cost ($)</label>
              <input type="number" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="e.g. 8000"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Salvage Value ($)</label>
              <input type="number" value={salvage} onChange={(e) => setSalvage(e.target.value)} placeholder="e.g. 500"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Method</label>
              <select value={method} onChange={(e) => setMethod(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-900">
                <option value="sl">Straight Line</option>
                <option value="ddb">Double Declining Balance</option>
              </select>
            </div>
          </div>
          {error ? <p className="text-red-600 text-sm mb-4">{error}</p> : null}
          <button onClick={calculate}
            className="bg-orange-600 hover:bg-orange-700 text-white font-semibold px-8 py-3 rounded-lg transition-colors">
            Calculate Depreciation
          </button>

          {results !== null ? (
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-orange-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-orange-700">{fmt(results.yr1)}</div>
                <div className="text-sm text-gray-600 mt-1">Year 1 Depreciation</div>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-blue-700">{FURNITURE_TYPES[fType].life} yrs</div>
                <div className="text-sm text-gray-600 mt-1">Useful Life</div>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-gray-700">{fmt(results.totalDepr)}</div>
                <div className="text-sm text-gray-600 mt-1">Total Depreciable Amount</div>
              </div>
            </div>
          ) : null}
          {schedule.length > 0 ? <DepreciationScheduleTable rows={schedule} onExport={() => exportToCSV(schedule, "furniture-depreciation-schedule.csv")} /> : null}
        </div>

        <div className="prose max-w-none">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Furniture & Appliance Depreciation — IRS Rules</h2>
          <p className="text-gray-700 mb-4">
            Office furniture and fixtures are classified as <strong>7-year MACRS property</strong>.
            Appliances used in a business (refrigerators, washers, etc.) are typically
            <strong> 5-year property</strong>. You can use either the straight line method or
            the accelerated double declining balance method.
          </p>
          <div className="overflow-x-auto rounded-xl border border-gray-200 mb-6">
            <table className="w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Asset</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">IRS Class</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Useful Life</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">SL Rate</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Desks, Chairs, Cubicles", "7-Year", "7 yrs", "14.29%"],
                  ["Filing Cabinets, Shelving", "7-Year", "7 yrs", "14.29%"],
                  ["Conference Room Furniture", "7-Year", "7 yrs", "14.29%"],
                  ["Washers / Dryers (business)", "5-Year", "5 yrs", "20.00%"],
                  ["Refrigerators (commercial)", "5-Year", "5 yrs", "20.00%"],
                  ["Carpeting / Flooring", "7-Year", "7 yrs", "14.29%"],
                ].map(([asset, cls, life, rate], i) => (
                  <tr key={asset} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="px-4 py-3 text-gray-700">{asset}</td>
                    <td className="px-4 py-3 text-orange-700 font-medium">{cls}</td>
                    <td className="px-4 py-3 text-gray-600">{life}</td>
                    <td className="px-4 py-3 text-gray-600">{rate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  )
}
